import { useRef } from 'react';

import { cn } from '@/lib/utils';

import { usePhoneHostRow } from '../internals/hostRow';
import { useFitsLine } from '../internals/useFitsLine';
import { sizingClass, TEXT_STYLE, TONE_CLASS, type Tone } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

// The tones that read as a secondary, meta-sized line; `default` and `rest` keep the
// body size
const SECONDARY_TONES: ReadonlySet<Tone> = new Set<Tone>([
  'muted',
  'subtle',
  'warn',
  'danger',
  'success',
  'act',
  'agent',
  'done',
  'fyi',
]);

export const Label: ComponentFn<typeof catalog, 'Label'> = ({ props }) => {
  const phoneHostRow = usePhoneHostRow();
  const measureRef = useRef<HTMLSpanElement>(null);
  // The full prefix must leave room for at least the title's ellipsis
  const prefixRoom = `${props.prefix ?? ''} …`;
  const prefixFits = useFitsLine(measureRef, prefixRoom);
  if (!props.text) return null;
  const tone = props.tone ?? 'default';
  const secondary = SECONDARY_TONES.has(tone);
  // The row's title reads as the phone heading, in full
  if (phoneHostRow && props.sizing === 'fill' && !secondary && !props.prefix) {
    return (
      <span
        className={cn(
          sizingClass(props.sizing),
          TEXT_STYLE.title,
          'min-w-0 break-words',
          TONE_CLASS[tone]
        )}
      >
        {props.text}
      </span>
    );
  }
  return (
    <span
      title={
        props.truncate
          ? [props.prefix, props.text].filter(Boolean).join(' ')
          : undefined
      }
      className={cn(
        sizingClass(props.sizing),
        secondary ? TEXT_STYLE.meta : TEXT_STYLE.body,
        TONE_CLASS[tone],
        props.weight === 'bold' && 'font-semibold',
        props.strike && 'line-through',
        props.mono && 'font-mono',
        props.truncate
          ? props.prefix
            ? 'relative flex min-w-0 gap-[var(--ac-gap-xs)] overflow-hidden'
            : 'block min-w-0 truncate'
          : secondary
            ? 'shrink-0'
            : 'min-w-0 break-words'
      )}
    >
      {props.prefix ? (
        <>
          {/* The title gives up its room first, then the identifier; the verb
          is never cut */}
          {props.labelWithoutIdentifier ? (
            <span
              ref={measureRef}
              aria-hidden="true"
              data-measure={prefixRoom}
              className="invisible pointer-events-none absolute left-0 top-0 whitespace-nowrap before:content-[attr(data-measure)]"
            />
          ) : null}
          <span className="shrink-0 whitespace-nowrap">
            {props.labelWithoutIdentifier && !prefixFits
              ? props.labelWithoutIdentifier
              : props.prefix}
          </span>{' '}
          <span className="min-w-0 truncate">{props.text}</span>
        </>
      ) : (
        props.text
      )}
    </span>
  );
};
